import * as THREE from 'three';

interface Plate {
  el: HTMLDivElement;
  hp: HTMLDivElement | null;
  marker: HTMLDivElement;
  visible: boolean;
}

interface FloatText {
  el: HTMLDivElement;
  pos: THREE.Vector3;
  t: number;
  dur: number;
  drift: number;
}

const v = new THREE.Vector3();

/**
 * DOM overlay for crisp nameplates, quest markers and floating combat text, positioned every frame
 * by projecting world positions. Elements are pooled to avoid layout churn.
 */
export class Overlay {
  readonly root: HTMLDivElement;
  private readonly plates = new Map<number, Plate>();
  private floats: FloatText[] = [];
  private readonly pool: HTMLDivElement[] = [];
  enabledNumbers = true;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'overlay';
    parent.appendChild(this.root);
  }

  addPlate(
    id: number,
    opts: { name: string; title?: string; level?: number; kind: string; showHp: boolean; self?: boolean },
  ): void {
    this.removePlate(id);
    const el = document.createElement('div');
    el.className = `plate plate-${opts.kind}${opts.self ? ' plate-self' : ''}`;
    const marker = document.createElement('div');
    marker.className = 'plate-marker';
    el.appendChild(marker);
    if (opts.title) {
      const t = document.createElement('div');
      t.className = 'plate-title';
      t.textContent = `‹${opts.title}›`;
      el.appendChild(t);
    }
    const n = document.createElement('div');
    n.className = 'plate-name';
    n.textContent = opts.level !== undefined ? `${opts.name}  Lv ${opts.level}` : opts.name;
    el.appendChild(n);
    let hp: HTMLDivElement | null = null;
    if (opts.showHp) {
      const bar = document.createElement('div');
      bar.className = 'plate-hp';
      hp = document.createElement('div');
      hp.className = 'plate-hp-fill';
      bar.appendChild(hp);
      el.appendChild(bar);
    }
    this.root.appendChild(el);
    this.plates.set(id, { el, hp, marker, visible: true });
  }

  removePlate(id: number): void {
    const p = this.plates.get(id);
    if (!p) return;
    p.el.remove();
    this.plates.delete(id);
  }

  setHp(id: number, frac: number): void {
    const p = this.plates.get(id);
    if (p?.hp) {
      p.hp.style.transform = `scaleX(${Math.max(0, Math.min(1, frac))})`;
      p.el.classList.toggle('damaged', frac < 0.999);
    }
  }

  setMarker(id: number, mark: string): void {
    const p = this.plates.get(id);
    if (!p) return;
    p.marker.textContent = mark;
    p.marker.className = `plate-marker ${mark === '!' ? 'avail' : mark === '?' ? 'ready' : mark ? 'progress' : ''}`;
  }

  setTargeted(id: number | null): void {
    for (const [pid, p] of this.plates) p.el.classList.toggle('targeted', pid === id);
  }

  setDim(id: number, dim: boolean): void {
    this.plates.get(id)?.el.classList.toggle('dim', dim);
  }

  floatText(pos: THREE.Vector3, text: string, cls: string, dur = 0.95): void {
    if (!this.enabledNumbers && cls.startsWith('dmg')) return;
    const el = this.pool.pop() ?? document.createElement('div');
    el.className = `float ${cls}`;
    el.textContent = text;
    el.style.opacity = '1';
    this.root.appendChild(el);
    this.floats.push({ el, pos: pos.clone(), t: 0, dur, drift: (Math.random() - 0.5) * 30 });
    if (this.floats.length > 40) this.recycle(this.floats.shift() as FloatText);
  }

  private recycle(f: FloatText): void {
    f.el.remove();
    if (this.pool.length < 40) this.pool.push(f.el);
  }

  update(
    dt: number,
    camera: THREE.Camera,
    w: number,
    h: number,
    headOf: (id: number) => THREE.Vector3 | null,
  ): void {
    for (const [id, p] of this.plates) {
      const head = headOf(id);
      let show = false;
      if (head) {
        v.copy(head).project(camera);
        show = v.z < 1 && v.x > -1.15 && v.x < 1.15 && v.y > -1.15 && v.y < 1.2;
        if (show) {
          const x = (v.x * 0.5 + 0.5) * w;
          const y = (-v.y * 0.5 + 0.5) * h;
          p.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -100%)`;
        }
      }
      if (show !== p.visible) {
        p.el.style.display = show ? '' : 'none';
        p.visible = show;
      }
    }
    const keep: FloatText[] = [];
    for (const f of this.floats) {
      f.t += dt;
      const u = f.t / f.dur;
      if (u >= 1) {
        this.recycle(f);
        continue;
      }
      v.copy(f.pos).project(camera);
      const x = (v.x * 0.5 + 0.5) * w + f.drift * u;
      const y = (-v.y * 0.5 + 0.5) * h - 70 * u - 10;
      const scale = u < 0.12 ? 0.6 + u * 5 : 1.2 - Math.min(0.25, (u - 0.12) * 0.4);
      f.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -50%) scale(${scale.toFixed(2)})`;
      f.el.style.opacity = String(u > 0.7 ? (1 - u) / 0.3 : 1);
      keep.push(f);
    }
    this.floats = keep;
  }

  dispose(): void {
    this.root.remove();
    this.plates.clear();
    this.floats = [];
  }
}
